import React, { ReactNode, ReactElement } from 'react'
import { Text, Grid, Box } from '@mindfulchefuk/design-system'
import { macroRounding } from '@mindfulchefuk/utils/macroRounding'
import { MacrosByPortionSize } from '@mindfulchefuk/features/Recipes/interfaces'

const Row = ({ children }: { children: ReactNode }) => (
  <>
    {children}
    <Seperator />
  </>
)

const Cell = ({ children }: { children: ReactNode }) => (
  <Text variant={['body-md']} textAlign="left" color="aubergine">
    {children}
  </Text>
)

const TitleCell = ({ children }: { children: ReactNode }) => (
  <Text variant={['body-md-500']} textAlign="left" color="aubergine" mt={4}>
    {children}
  </Text>
)

const CellDoubleHeight = ({ children }: { children: ReactNode }) => (
  <Text
    variant={['body-md']}
    textAlign="left"
    color="aubergine"
    gridRow="span 2"
  >
    {children}
  </Text>
)

const Seperator = (): ReactElement => (
  <Box
    gridColumn="1 / -1"
    height={2}
    borderBottomWidth={1}
    borderColor="pepper-50"
    width="100%"
    marginTop={8}
    marginBottom={8}
  />
)

export const RecipeMacrosGrid = ({
  macrosPer100g,
  macrosPerPerson,
}: MacrosByPortionSize): ReactElement => (
  <Grid
    templateColumns="1fr 1fr 1fr"
    columnGap={[12]}
    rowGap={4}
    alignItems="center"
    justifyContent="center"
    border="1px solid aubergine"
    mt={4}
  >
    <Row>
      <TitleCell>Typical Values</TitleCell>
      <TitleCell>per 100g</TitleCell>
      <TitleCell>per portion</TitleCell>
    </Row>
    <Row>
      <CellDoubleHeight>Energy</CellDoubleHeight>
      <Cell>{macroRounding.energy(macrosPer100g.kilojoules as number)}kJ</Cell>
      <Cell>
        {macroRounding.energy(macrosPerPerson.kilojoules as number)}kJ
      </Cell>
      <Cell>{macroRounding.energy(macrosPer100g.calories as number)}Kcal</Cell>
      <Cell>
        {macroRounding.energy(macrosPerPerson.calories as number)}Kcal
      </Cell>
    </Row>
    <Row>
      <Cell>Fat</Cell>
      <Cell>{macroRounding.fat(macrosPer100g.fat as number)}g</Cell>
      <Cell>{macroRounding.fat(macrosPerPerson.fat as number)}g</Cell>
      <Cell>of which saturates</Cell>
      <Cell>
        {macroRounding.saturatedFat(macrosPer100g.saturatedFat as number)}g
      </Cell>
      <Cell>
        {macroRounding.saturatedFat(macrosPerPerson.saturatedFat as number)}g
      </Cell>
    </Row>
    <Row>
      <Cell>Carbohydrates</Cell>
      <Cell>{macroRounding.sugar(macrosPer100g.carbohydrates as number)}g</Cell>
      <Cell>
        {macroRounding.sugar(macrosPerPerson.carbohydrates as number)}g
      </Cell>
      <Cell>of which sugars</Cell>
      <Cell>{macroRounding.sugar(macrosPer100g.sugars as number)}g</Cell>
      <Cell>{macroRounding.sugar(macrosPerPerson.sugars as number)}g</Cell>
    </Row>
    <Row>
      <Cell>Protein</Cell>
      <Cell>{macroRounding.protein(macrosPer100g.protein as number)}g</Cell>
      <Cell>{macroRounding.protein(macrosPerPerson.protein as number)}g</Cell>
    </Row>
    <Row>
      <Cell>Salt</Cell>
      <Cell>{macroRounding.salt(macrosPer100g.salt as number)}g</Cell>
      <Cell>{macroRounding.salt(macrosPerPerson.salt as number)}g</Cell>
    </Row>
    <Cell>Fibre</Cell>
    <Cell>{macroRounding.fibre(macrosPer100g.fibre as number)}g</Cell>
    <Cell>{macroRounding.fibre(macrosPerPerson.fibre as number)}g</Cell>
  </Grid>
)
